/**
 * How long a rate-limited (853) user waits, in words. Framework-free (copied
 * to next-mapit).
 *
 * The per-minute limit waits until the next minute (≤ 60 s): a seconds
 * countdown. The daily limits wait until midnight in Riyadh (up to 24 h): a
 * rounded "about N minutes / hours", which `Intl` words with the right
 * plural form in both languages («دقيقتان», «٣ ساعات», «١١ ساعة»). The
 * digits follow the locale, like the dates (Q4).
 */

/** Longest wait shown as a seconds countdown; a longer one is a daily limit. */
export const COUNTDOWN_MAX_SECONDS = 60;

/** `describeWait().kind`. */
export const WAIT_KINDS = Object.freeze({
  SECONDS: "seconds",
  LONG: "long",
});

const formatters = new Map();

/**
 * `"12 hours"` / «١٢ ساعة». Browsers without unit formatting (Safari < 14.1)
 * get the number and a short Latin unit; an invalid language falls back to
 * the runtime's locale.
 */
const formatUnit = (value, unit, lang) => {
  const key = `${unit}|${lang}`;
  if (!formatters.has(key)) {
    let formatter = null;
    for (const locale of lang ? [lang, undefined] : [undefined]) {
      try {
        formatter = new Intl.NumberFormat(locale, {
          style: "unit",
          unit,
          unitDisplay: "long",
        });
        break;
      } catch {
        // RangeError: an unknown language, or no unit support at all
      }
    }
    formatters.set(key, formatter);
  }
  const formatter = formatters.get(key);
  if (formatter) return formatter.format(value);
  return `${value} ${unit === "hour" ? "h" : "min"}`;
};

/**
 * @param {number} seconds Seconds left (anything else counts as 0).
 * @param {string} [lang] BCP 47 tag, e.g. `i18n.language`.
 * @returns {{ kind: "seconds", seconds: number } | { kind: "long", text: string }}
 *   `seconds` is a whole number ≥ 0; `text` is minutes (rounded up) under an
 *   hour, else hours (rounded, at least 1).
 */
export function describeWait(seconds, lang) {
  const left = Number.isFinite(seconds) ? Math.max(0, Math.ceil(seconds)) : 0;
  if (left <= COUNTDOWN_MAX_SECONDS) {
    return { kind: WAIT_KINDS.SECONDS, seconds: left };
  }
  const minutes = Math.ceil(left / 60);
  const text =
    minutes < 60
      ? formatUnit(minutes, "minute", lang)
      : formatUnit(Math.max(1, Math.round(left / 3600)), "hour", lang);
  return { kind: WAIT_KINDS.LONG, text };
}
